// client/src/components/pages/AmendmentsAllocateTransactions/UnmatchedCorrectionsTab.tsx
//
// ST-00212: the Amendments page's "Unmatched corrections" tab — the review list. A correction
// whose ticket the upstream re-issued, and which the follow-source job could not re-attach by
// itself (no ticket, more than one, another weigh time, the match already corrected, a zero nett
// weight), waits here until someone attaches it to the suggested ticket or archives it.
// GET /api/amendments/v3/unmatched; Attach / Archive need amendments.edit. A ticket that already
// has a correction of its own is never merged (founder, 2026-11-04): Attach is off for
// `target_amended`, and the server answers 409 with its reason if it refuses.
import { useCallback, useEffect, useState } from 'react';
import { FaArchive, FaLink, FaSpinner } from 'react-icons/fa';
import ThemedSelect from '../../common/ThemedSelect';
import {
  archiveUnmatched,
  attachUnmatched,
  followSourceErrorText,
  getUnmatched,
} from '../../../services/amendmentFollowSource';
import type {
  ReviewReason,
  UnmatchedCorrection,
  UnmatchedResponse,
} from '../../../types/amendmentFollowSource';
import { formatAppDateTime, formatMiningDateTime } from '../../../utils/dateUtils';
import {
  REVIEW_REASONS,
  REVIEW_REASON_HELP,
  REVIEW_REASON_LABEL,
  formatCount,
  formatFieldValue,
  formatTonnes,
  routeText,
} from './followSourceFormat';
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  Banner,
  EmptyRow,
  ListPager,
  TABLE,
  TBODY,
  TD,
  TH,
  THEAD,
} from './FollowSourceParts';

const PAGE_SIZE = 100;

export interface UnmatchedCorrectionsTabProps {
  /** amendments.edit: the Attach and Archive buttons. */
  canEdit: boolean;
  /** How many corrections wait on the review list (every reason), for the tab label. */
  onCountChange?: (count: number) => void;
}

/** The question Attach asks before it attaches (a zero nett says the ticket will count as 0 t). */
export function attachConfirmText(item: UnmatchedCorrection): string {
  const s = item.suggested!;
  const lines = [
    `Attach the correction of ticket ${item.ticketNo || item.previousTicketUID} to ticket ${
      s.ticketNo || s.ticketUID
    } (${formatMiningDateTime(s.ticketTimestamp) || 'weigh time unknown'}, ${routeText(
      s.dispatchLocation,
      s.receiptLocation
    )})?`,
  ];
  if (item.reason === 'zero_nett') {
    lines.push(
      'This correction sets the nett weight to 0: the ticket will count as 0 t in every total and report.'
    );
  }
  lines.push(
    'The corrected fields then apply to that ticket. If the source later changes one of them, the source wins again.'
  );
  return lines.join('\n\n');
}

const sumCounts = (byReason: UnmatchedResponse['byReason']): number =>
  Object.values(byReason).reduce<number>((sum, n) => sum + (n ?? 0), 0);

const UnmatchedCorrectionsTab = ({ canEdit, onCountChange }: UnmatchedCorrectionsTabProps) => {
  const [reason, setReason] = useState<ReviewReason | ''>('');
  const [offset, setOffset] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);
  const [data, setData] = useState<UnmatchedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<UnmatchedCorrection | null>(null);
  const [archiveNotes, setArchiveNotes] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    getUnmatched(
      { reason: reason || null, limit: PAGE_SIZE, offset },
      { signal: controller.signal }
    )
      .then((body) => {
        if (controller.signal.aborted) return;
        setData(body);
        // byReason counts every reason, whatever the filter.
        onCountChange?.(sumCounts(body.byReason));
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(followSourceErrorText(err, 'Could not load the unmatched corrections.'));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // onCountChange is a callback prop: re-running on its identity would refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reason, offset, reloadKey]);

  const attach = useCallback(async (item: UnmatchedCorrection) => {
    if (!item.suggested) return;
    if (!window.confirm(attachConfirmText(item))) return;
    setBusyId(item.anchorId);
    setActionError(null);
    setNotice(null);
    try {
      const res = await attachUnmatched(item.anchorId, item.suggested.ticketUID);
      setNotice(
        `${res.message || 'Correction attached'}: ticket ${item.suggested.ticketNo || item.suggested.ticketUID}.`
      );
    } catch (err) {
      setActionError(followSourceErrorText(err, 'Could not attach the correction.'));
    } finally {
      setBusyId(null);
      setReloadKey((k) => k + 1);
    }
  }, []);

  const closeArchive = () => {
    setArchiveTarget(null);
    setArchiveNotes('');
  };

  const archive = async () => {
    const item = archiveTarget;
    if (!item) return;
    setBusyId(item.anchorId);
    setActionError(null);
    setNotice(null);
    try {
      const res = await archiveUnmatched(item.anchorId, archiveNotes);
      setNotice(
        `${res.message || 'Correction archived'}: ticket ${item.ticketNo || item.previousTicketUID}.`
      );
      closeArchive();
    } catch (err) {
      setActionError(followSourceErrorText(err, 'Could not archive the correction.'));
      closeArchive();
    } finally {
      setBusyId(null);
      setReloadKey((k) => k + 1);
    }
  };

  const items = data?.items ?? [];
  const byReason = data?.byReason ?? {};
  const columns = canEdit ? 8 : 7;

  return (
    <div>
      <p className="px-4 py-2 text-xs text-slate-500 border-b border-slate-200">
        When the upstream system re-issues a ticket, its corrections move to the new ticket by
        themselves. The corrections below could not be moved safely, so they count on no ticket
        until someone checks them: attach each one to the ticket it belongs to, or archive it.
      </p>

      <div className="flex flex-wrap items-center gap-3 px-4 py-2 border-b border-slate-200 bg-slate-50/50 text-xs text-slate-600">
        <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Reason</span>
        <div className="min-w-[16rem]">
          <ThemedSelect
            id="unmatched-reason"
            aria-label="Filter by reason"
            value={reason}
            onChange={(e) => {
              setReason(String(e.target.value) as ReviewReason | '');
              setOffset(0);
            }}
            className="text-xs"
          >
            <option value="">All reasons ({formatCount(sumCounts(byReason))})</option>
            {REVIEW_REASONS.map((r) => (
              <option key={r} value={r}>
                {REVIEW_REASON_LABEL[r]} ({formatCount(byReason[r] ?? 0)})
              </option>
            ))}
          </ThemedSelect>
        </div>
      </div>

      {error && <Banner tone="error">{error}</Banner>}
      {actionError && (
        <Banner tone="error" onDismiss={() => setActionError(null)}>
          {actionError}
        </Banner>
      )}
      {notice && (
        <Banner tone="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Banner>
      )}

      <div className="overflow-x-auto">
        <table className={TABLE}>
          <thead className={THEAD}>
            <tr>
              <th className={TH}>Ticket No</th>
              <th className={TH}>Weigh time</th>
              <th className={TH}>Route</th>
              <th className={TH}>Corrections</th>
              <th className={TH}>Corrected by</th>
              <th className={TH}>Reason</th>
              <th className={TH}>Suggested ticket</th>
              {canEdit && <th className={TH}>Actions</th>}
            </tr>
          </thead>
          <tbody className={TBODY}>
            {loading && !data ? (
              <EmptyRow colSpan={columns}>
                <FaSpinner className="inline w-3.5 h-3.5 mr-1.5 animate-spin" aria-hidden="true" />
                Loading unmatched corrections…
              </EmptyRow>
            ) : items.length === 0 && !error ? (
              <EmptyRow colSpan={columns}>
                {reason
                  ? 'No corrections wait for this reason.'
                  : 'Every correction is on its ticket: nothing to review.'}
              </EmptyRow>
            ) : (
              items.map((item) => {
                const s = item.suggested;
                const attachBlocked = item.reason === 'target_amended';
                return (
                  <tr key={item.anchorId} data-testid={`unmatched-${item.anchorId}`}>
                    <td className={`${TD} font-mono whitespace-nowrap`}>{item.ticketNo || '—'}</td>
                    <td className={`${TD} whitespace-nowrap tabular-nums`}>
                      {formatMiningDateTime(item.ticketTimestamp) || '—'}
                    </td>
                    <td className={TD}>
                      {routeText(item.dispatchLocation, item.receiptLocation)}
                      {item.ticketType && (
                        <span className="block text-[11px] text-slate-400">{item.ticketType}</span>
                      )}
                    </td>
                    <td className={TD}>
                      {item.corrections.length === 0 ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <ul className="space-y-0.5">
                          {item.corrections.map((c) => (
                            <li key={c.field} className="whitespace-nowrap">
                              <span className="text-slate-500">{c.field}:</span>{' '}
                              <span className="font-medium text-slate-900">
                                {formatFieldValue(c.value)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className={TD}>
                      <span className="font-medium">{item.amendmentUser || 'Not recorded'}</span>
                      {item.amendmentTimestamp && (
                        <span className="block tabular-nums text-slate-500">
                          {formatAppDateTime(item.amendmentTimestamp)}
                        </span>
                      )}
                      {item.amendmentNotes && (
                        <span className="block italic text-slate-500">
                          &ldquo;{item.amendmentNotes}&rdquo;
                        </span>
                      )}
                    </td>
                    <td className={`${TD} min-w-[12rem]`}>
                      <span className="font-medium text-slate-900">
                        {REVIEW_REASON_LABEL[item.reason] || item.reason}
                      </span>
                      {!s && (
                        <span className="block text-slate-500">
                          {REVIEW_REASON_HELP[item.reason]}
                        </span>
                      )}
                    </td>
                    <td className={`${TD} min-w-[12rem]`}>
                      {s ? (
                        <>
                          <span className="font-mono">{s.ticketNo || s.ticketUID}</span>
                          <span className="block tabular-nums text-slate-500">
                            {formatMiningDateTime(s.ticketTimestamp) || '—'}
                          </span>
                          <span className="block">
                            {routeText(s.dispatchLocation, s.receiptLocation)}
                          </span>
                          <span className="block tabular-nums">
                            Nett {formatTonnes(s.nettWeight)}
                          </span>
                          {s.note && <span className="block mt-1 text-slate-500">{s.note}</span>}
                        </>
                      ) : (
                        <span className="text-slate-400">No suggestion</span>
                      )}
                    </td>
                    {canEdit && (
                      <td className={`${TD} whitespace-nowrap`}>
                        <div className="flex flex-col items-start gap-1.5">
                          <button
                            type="button"
                            className={BUTTON_PRIMARY}
                            disabled={!s || attachBlocked || busyId !== null}
                            title={
                              !s
                                ? 'There is no ticket to attach this correction to.'
                                : attachBlocked
                                  ? 'That ticket already has a correction of its own. Corrections are never merged: archive one of the two.'
                                  : undefined
                            }
                            onClick={() => attach(item)}
                          >
                            {busyId === item.anchorId ? (
                              <FaSpinner className="w-3 h-3 animate-spin" aria-hidden="true" />
                            ) : (
                              <FaLink className="w-3 h-3" aria-hidden="true" />
                            )}
                            Attach to suggested ticket
                          </button>
                          <button
                            type="button"
                            className={BUTTON_SECONDARY}
                            disabled={busyId !== null}
                            onClick={() => {
                              setArchiveNotes('');
                              setArchiveTarget(item);
                            }}
                          >
                            <FaArchive className="w-3 h-3" aria-hidden="true" />
                            Archive
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {data && (
        <ListPager
          offset={offset}
          pageSize={PAGE_SIZE}
          shown={items.length}
          total={data.total}
          onOffsetChange={setOffset}
        />
      )}

      {archiveTarget && (
        <div
          className="fixed inset-0 z-50 bg-gray-900/60 dark:bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onKeyDown={(e) => {
            if (e.key === 'Escape' && busyId === null) closeArchive();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="archive-correction-title"
            className="w-full max-w-md rounded-lg border border-slate-200 bg-surface p-5 shadow-xl"
          >
            <h2 id="archive-correction-title" className="text-sm font-semibold text-slate-900">
              Archive this correction?
            </h2>
            <p className="mt-2 text-xs text-slate-600">
              The correction of ticket{' '}
              <span className="font-mono font-medium">
                {archiveTarget.ticketNo || archiveTarget.previousTicketUID}
              </span>{' '}
              leaves the review list and is not applied to any ticket. It stays in the ticket&apos;s
              history: nothing is deleted.
            </p>
            <label
              htmlFor="archive-correction-notes"
              className="mt-4 block text-xs font-medium text-slate-700"
            >
              Notes (optional)
            </label>
            <textarea
              id="archive-correction-notes"
              rows={3}
              maxLength={1000}
              value={archiveNotes}
              onChange={(e) => setArchiveNotes(e.target.value)}
              placeholder="Why it is archived, e.g. the ticket was cancelled upstream"
              className="mt-1 block w-full rounded-md border border-slate-300 bg-surface px-2.5 py-1.5 text-xs text-slate-900 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              autoFocus
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className={BUTTON_SECONDARY}
                onClick={closeArchive}
                disabled={busyId !== null}
              >
                Cancel
              </button>
              <button
                type="button"
                className={BUTTON_PRIMARY}
                onClick={archive}
                disabled={busyId !== null}
              >
                {busyId !== null && (
                  <FaSpinner className="w-3 h-3 animate-spin" aria-hidden="true" />
                )}
                Archive correction
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnmatchedCorrectionsTab;
